import { toast } from '@gmg/gmg-react-components';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEditLicenseSystem } from 'src/graphql/useEditLicenseSystem';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import { TargetLicenseSystemData } from './LicenseManagementAction';
import LicensesAccordion, { LicenseStatus, OptionStatus } from './LicensesAccordion';
import LicenseSystemRow from './LicenseSystemRow';
import { addDaysToDate } from 'src/shared/dateManipulator';

export interface LicenseManagementRowProps {
    licenseSystem: LicenseSystem;
    isExpiredLicensesFilterActive: boolean;
    targetLicenseSystems: Array<TargetLicenseSystemData>;
    isExpanded: boolean;
    isAllowedToUseLicenseSystemsOffline: boolean;
    onArrowClick: (id: string) => void;
};

function getLicenseStatus(licenseExpiration: string): LicenseStatus {

    const licenseExpirationDate = new Date(licenseExpiration);
    const today = new Date();
    const in30Days = addDaysToDate(today, 30);
    // Expiration date is set to "9999-12-31T22:59:59.99Z" in case of unlimited expiration time.
    const dayFarInFuture = addDaysToDate(today, 100 * 365);

    if (licenseExpirationDate < today) return 'expired';
    if (licenseExpirationDate < in30Days) return 'willExpireSoon';
    if (licenseExpirationDate > dayFarInFuture) return 'unlimited';
    return 'willExpire';
};

function getOptionStatus(licenseExpiration: string, optionExpiration: string | undefined): OptionStatus {

    if (!optionExpiration) return 'notShown';

    const licenseExpirationDate = new Date(licenseExpiration);
    const optionExpirationDate = new Date(optionExpiration);
    const isOptionExpirationDateBeforeLicenseExpirationDate =
        optionExpirationDate < licenseExpirationDate;

    if (!isOptionExpirationDateBeforeLicenseExpirationDate) return 'notShown';

    const today = new Date();
    const in30Days = addDaysToDate(today, 30);

    if (optionExpirationDate < today) return 'expired';
    // required for the warning sign shown for the entire license system
    if (optionExpirationDate < in30Days) return 'willExpireSoon';

    return 'willExpire';
};

const LicenseSystemRowContainer: FunctionComponent<LicenseManagementRowProps> = ({
    licenseSystem,
    isExpanded: isLicensesAccordionExpanded,
    isExpiredLicensesFilterActive,
    onArrowClick: onLicensesInfoArrowClick,
    targetLicenseSystems,
    isAllowedToUseLicenseSystemsOffline,
}) => {

    const { t } = useTranslation();
    const { orgId } = useParams();
    const editLicenseSystem = useEditLicenseSystem(licenseSystem.id, orgId!);
    const originalLocation = licenseSystem.location;
    const [location, setLocation] = useState<string>(originalLocation);

    // Updates the location if the organization data changes, i.e. get organization query has been invalidated.
    useEffect(() => {
        setLocation(originalLocation);
    }, [originalLocation]);

    const onChangeLicenseSystemLocation = () => {
        if (location === originalLocation) return;
        const pendingPromise = editLicenseSystem.mutateAsync(location);

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: (t('LicenseManagementTable.edited_location_success_toast_desc', {
                defaultValue: 'You have successfully edited the location of license system "{{serialNumber}}"!',
                serialNumber: licenseSystem.id,
            })),
            error: () => {
                setLocation(originalLocation);
                return t('Common.Toast.Error', 'An error occurred');
            },
        });
    };

    const licenseSystemAttributes = {
        id: licenseSystem.id,
        userName: licenseSystem.userName,
        userEmail: licenseSystem.userEmail,
        licenseType: licenseSystem.licenseType,
        location,
        computerName: licenseSystem.computerName,
        isTransferSupported: licenseSystem.isTransferSupported,
        hasPendingProcess: licenseSystem.hasPendingProcess,
        isLicenseSystemUpdateAvailable: licenseSystem.isLicenseSystemUpdateAvailable,
        hasActiveSuc: licenseSystem.hasActiveSuc,
        cpGo: licenseSystem.cpGo,
    };

    // Expired licenses must NOT be filtered out here.
    const isLicenseSystemEmpty = licenseSystem.licenses.length === 0;

    const licensesWithoutExpired = licenseSystem.licenses.filter((lic) => new Date(lic.expirationDate) >= new Date());

    const shownLicenses = isExpiredLicensesFilterActive
        ? licensesWithoutExpired
        : licenseSystem.licenses;

    const licensesShownInAccordion = shownLicenses.map(license => {

        return {
            ...license,
            status: getLicenseStatus(license.expirationDate),
            licenseOptions: license.licenseOptions.map(option => ({
                ...option,
                status: getOptionStatus(license.expirationDate, option.expirationDate),
            })),
        };
    });

    const isLicensesAccordionEmpty = licensesShownInAccordion.length === 0;

    const isInStatusWarning = licensesShownInAccordion.some(license =>
        license.status === 'willExpireSoon' ||
        license.licenseOptions.some(option => option.status === 'willExpireSoon'),
    );

    return (
        <div>
            <LicenseSystemRow
                attributes={licenseSystemAttributes}
                isExpanded={isLicensesAccordionExpanded}
                isLicensesAccordionEmpty={isLicensesAccordionEmpty}
                isInStatusWarning={isInStatusWarning}
                isEditingPending={editLicenseSystem.isPending}
                isRequestTemporaryLicensesButtonShown={isLicenseSystemEmpty}
                targetLicenseSystems={targetLicenseSystems}
                onArrowClick={onLicensesInfoArrowClick}
                setLocation={setLocation}
                onChangeLocation={onChangeLicenseSystemLocation}
                isAllowedToUseLicenseSystemsOffline={isAllowedToUseLicenseSystemsOffline}
            />
            <LicensesAccordion
                isExpanded={isLicensesAccordionExpanded}
                licenses={licensesShownInAccordion}
                licenseSystemId={licenseSystem.id}
            />
        </div>
    );
};

export default LicenseSystemRowContainer;